// Process JSON tour data to chart-ready format
export function processTourData(jsonResponse) {
    if (!jsonResponse.tours) {
        throw new Error('Invalid JSON response format')
    }
    
    const tours = jsonResponse.tours

    // Convert tours to standardized format
    const processedData = tours.map(tour => ({
        date: new Date(tour.date),
        distance: parseFloat(tour.distance_km),
        duration_seconds: parseInt(tour.duration_seconds),
        speed: parseFloat(tour.speed_kmh),
        year: new Date(tour.date).getFullYear(),
        month: new Date(tour.date).getMonth() + 1
    }))
    
    return processedData.sort((a, b) => a.date - b.date)
}

// Convert processed tour data to chart dataset format
export function getDataset(tours) {
    // Aggregate data points in case of duplicate dates
    const aggregated = aggregateByDate(tours)
    
    // Resample data to daily frequency
    const daily = resampleToDaily(aggregated)
    
    // Calculate cumulative sums
    const withCumsums = calculateCumulativeSums(daily)
    
    // Format output by year
    return formatByYear(withCumsums)
}

function aggregateByDate(data) {
    const grouped = {}
    
    data.forEach(row => {
        const dateKey = row.date.toISOString().split('T')[0]
        if (!grouped[dateKey]) {
            grouped[dateKey] = {
                date: row.date,
                distance: 0,
                duration_seconds: 0,
                speed: 0,
                count: 0,
                year: row.year,
                month: row.month
            }
        }
        
        grouped[dateKey].distance += row.distance || 0
        grouped[dateKey].duration_seconds += row.duration_seconds || 0
        grouped[dateKey].speed += row.speed || 0
        grouped[dateKey].count += 1
    })
    
    // Calculate average speed
    Object.values(grouped).forEach(record => {
        record.speed = record.count > 0 ? record.speed / record.count : 0
    })
    
    return Object.values(grouped)
}

function resampleToDaily(aggregated) {
    // Create a map of all dates in the range
    const dateMap = new Map()
    const startDate = new Date(Math.min(...aggregated.map(r => r.date)))
    const endDate = new Date(Math.max(...aggregated.map(r => r.date)))
    
    for (let d = new Date(startDate); d <= endDate; d.setUTCDate(d.getUTCDate() + 1)) {
        const dateKey = d.toISOString().split('T')[0]
        dateMap.set(dateKey, {
            date: new Date(d),
            distance: null,
            duration_seconds: null,
            speed: null,
            year: d.getUTCFullYear(),
            month: d.getUTCMonth() + 1,
            cumsum_year: 0,
            cumsum_month: 0,
            cumsum_duration_month: 0
        })
    }
    
    // Fill in actual data
    aggregated.forEach(record => {
        const dateKey = record.date.toISOString().split('T')[0]
        if (dateMap.has(dateKey)) {
            const daily = dateMap.get(dateKey)
            daily.distance = record.distance
            daily.duration_seconds = record.duration_seconds
            daily.speed = record.speed
        }
    })
    
    return Array.from(dateMap.values())
}

function calculateCumulativeSums(daily) {
    // Group by year for yearly cumulative
    const byYear = {}
    daily.forEach(record => {
        if (!byYear[record.year]) byYear[record.year] = []
        byYear[record.year].push(record)
    })
    
    // Calculate cumulative sums
    Object.values(byYear).forEach(yearData => {
        let cumsumYear = 0
        let cumsumMonth = 0
        let cumsumDurationMonth = 0
        let currentMonth = null
        
        yearData.forEach(record => {
            if (record.distance !== null) {
                cumsumYear += record.distance
            }
            
            if (currentMonth !== record.month) {
                cumsumMonth = 0
                cumsumDurationMonth = 0
                currentMonth = record.month
            }
            
            if (record.distance !== null) {
                cumsumMonth += record.distance
            }
            if (record.duration_seconds !== null) {
                cumsumDurationMonth += record.duration_seconds
            }
            
            record.cumsum_year = cumsumYear
            record.cumsum_month = cumsumMonth
            record.cumsum_duration_month = cumsumDurationMonth
        })
    })
    
    return daily
}

export function computeStats(visibleYears, fullDataset) {
    const empty = {
        totalDistance: 0, totalRides: 0, totalDurationSec: 0, avgSpeed: 0,
        firstYear: null, lastYear: null, yearCount: 0, avgRidesPerYear: 0,
        distDeltaText: null, ridesDeltaText: null,
    }
    if (!visibleYears || visibleYears.length === 0) return empty

    let totalDistance = 0
    let totalRides = 0
    let totalDurationSec = 0

    visibleYears.forEach(y => {
        const rows = y.source.slice(1)
        const yearDist = rows.length > 0 ? (rows[rows.length - 1][5] || 0) : 0
        totalDistance += yearDist
        rows.forEach(r => {
            if (r[3] && r[3] > 0) {
                totalRides += 1
                totalDurationSec += r[7] || 0
            }
        })
    })

    const avgSpeed = totalDurationSec > 0 ? totalDistance / (totalDurationSec / 3600) : 0
    const yearIds = visibleYears.map(y => parseInt(y.id)).sort((a, b) => a - b)
    const firstYear = yearIds[0]
    const lastYear = yearIds[yearIds.length - 1]
    const yearCount = visibleYears.length
    const avgRidesPerYear = yearCount > 0 ? totalRides / yearCount : 0

    let distDeltaText = null
    let ridesDeltaText = null
    const priorYearId = String(lastYear - 1)
    const latestVisible = visibleYears.find(y => y.id === String(lastYear))
    const priorInFull = fullDataset?.find(y => y.id === priorYearId)
    if (latestVisible && priorInFull) {
        const latestRows = latestVisible.source.slice(1)
        const priorRows = priorInFull.source.slice(1)
        const currentYear = new Date().getFullYear()
        const isCurrentSeason = lastYear === currentYear
        const suffix = isCurrentSeason ? 'vs last ytd' : 'vs last yr'
        const cutoffMmdd = isCurrentSeason ? todayMmdd() : '12-31'

        const latestDist = cumDistUpTo(latestRows, cutoffMmdd)
        const priorDist = cumDistUpTo(priorRows, cutoffMmdd)
        const dDiff = latestDist - priorDist
        distDeltaText = `${dDiff >= 0 ? '↑' : '↓'} ${fmtDistance(Math.abs(dDiff))}km ${suffix}`

        const latestRideCount = rideCountUpTo(latestRows, cutoffMmdd)
        const priorRideCount = rideCountUpTo(priorRows, cutoffMmdd)
        const rDiff = latestRideCount - priorRideCount
        ridesDeltaText = `${rDiff >= 0 ? '↑' : '↓'} ${fmtInt(Math.abs(rDiff))} ${suffix}`
    }

    return {
        totalDistance, totalRides, totalDurationSec, avgSpeed,
        firstYear, lastYear, yearCount, avgRidesPerYear,
        distDeltaText, ridesDeltaText,
    }
}

export function fmtDistance(km) {
    if (km == null || Number.isNaN(km)) return '—'
    if (km >= 100) return euThousands(Math.round(km))
    return km.toFixed(1).replace('.', ',')
}

// Per-ride distance — always one decimal so the list column reads evenly.
// (fmtDistance drops the decimal at >=100km, which looks ragged row to row.)
export function fmtRideDistance(km) {
    if (km == null || Number.isNaN(km)) return '—'
    return Number(km).toFixed(1).replace('.', ',')
}

export function fmtSpeed(kmh) {
    if (kmh == null || Number.isNaN(kmh) || kmh === 0) return '—'
    return kmh.toFixed(1).replace('.', ',')
}

export function fmtInt(n) {
    if (n == null || Number.isNaN(n)) return '—'
    return euThousands(Math.round(n))
}

export function fmtDecimal1(n) {
    if (n == null || Number.isNaN(n)) return '—'
    return n.toFixed(1).replace('.', ',')
}

function euThousands(n) {
    const s = String(Math.abs(n))
    const withDots = s.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return n < 0 ? '-' + withDots : withDots
}

function todayMmdd() {
    const d = new Date()
    const pad = n => String(n).padStart(2, '0')
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function cumDistUpTo(rows, mmdd) {
    let last = 0
    for (const r of rows) {
        if (r[0].slice(5) > mmdd) break
        last = r[5] || last
    }
    return last
}

function rideCountUpTo(rows, mmdd) {
    let count = 0
    for (const r of rows) {
        if (r[0].slice(5) > mmdd) break
        if (r[3] && r[3] > 0) count += 1
    }
    return count
}

export function fmtShortDate(iso) {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    const pad = n => String(n).padStart(2, '0')
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`
}

export function fmtShortTime(iso) {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    const pad = n => String(n).padStart(2, '0')
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// True when the date string carries a time component (full ISO timestamp).
// Date-only records (YYYY-MM-DD) return false.
export function hasTime(dateStr) {
    return /T\d{2}:\d{2}/.test(String(dateStr))
}

// DD.MM.YY for the ride list. Date-only strings are sliced (TZ-safe, no drift);
// timestamped strings go through fmtShortDate so the date matches the local
// HH:MM rendered alongside it.
export function fmtRideDate(dateStr) {
    if (hasTime(dateStr)) return fmtShortDate(dateStr) ?? '—'
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr))
    return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : (fmtShortDate(dateStr) ?? '—')
}

// Ride duration as H:MM:SS (hours unpadded). Missing/zero → em dash.
export function fmtDuration(seconds) {
    if (seconds == null || Number.isNaN(seconds) || seconds <= 0) return '—'
    const total = Math.round(seconds)
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    const pad = n => String(n).padStart(2, '0')
    return `${h}:${pad(m)}:${pad(s)}`
}

// Lenient decimal parse for form fields — accepts a comma or dot separator
// (the list renders EU-style commas). null when blank or not a number.
export function parseDecimal(value) {
    if (value == null) return null
    const s = String(value).trim().replace(',', '.')
    if (s === '') return null
    const n = Number(s)
    return Number.isFinite(n) ? n : null
}

// Parse a single duration field into seconds. Accepts `h:mm:ss`, `m:ss`, or a
// bare number (minutes). '' → 0; non-numeric → null (invalid).
export function parseDuration(value) {
    const s = String(value ?? '').trim()
    if (s === '') return 0
    const parts = s.split(':').map(p => p.trim())
    if (parts.length > 3) return null
    const nums = parts.map(p => (p === '' ? 0 : Number(p)))
    if (nums.some(n => !Number.isFinite(n) || n < 0)) return null
    let h = 0
    let sec = 0
    let m
    if (nums.length === 3) [h, m, sec] = nums
    else if (nums.length === 2) [m, sec] = nums
    else [m] = nums
    return h * 3600 + m * 60 + sec
}

// Avg speed from distance + duration; null when either is missing. Two decimals,
// matching normaliseRides.
export function avgSpeedKmh(distanceKm, durationSeconds) {
    if (!distanceKm || !durationSeconds || durationSeconds <= 0) return null
    return Math.round((distanceKm / (durationSeconds / 3600)) * 100) / 100
}

// Adapter source id → uppercase display tag for the ride list.
// `muted` marks the demo source, which renders in --mute.
const SOURCE_TAGS = {
    'wahoo': { label: 'WAHOO' },
    'manual': { label: 'MANUAL' },
    'json-import': { label: 'IMPORT' },
    'custom-url': { label: 'CUSTOM' },
    'demo-data': { label: 'DEMO', muted: true },
}

export function sourceTag(source) {
    const key = String(source ?? '').toLowerCase()
    return SOURCE_TAGS[key] ?? { label: key ? key.toUpperCase() : '—' }
}

function formatByYear(daily) {
    const byYear = {}
    daily.forEach(record => {
        if (!byYear[record.year]) {
            byYear[record.year] = {
                id: record.year.toString(),
                source: [
                    ['timestamp', 'normalized_date', 'month', 'distance', 'cumsum_month', 'cumsum_year', 'speed', 'duration_seconds', 'cumsum_duration_month']
                ]
            }
        }
        
        // UTC string surgery — setFullYear is local-time and drifts at TZ boundaries.
        const isoDate = record.date.toISOString().slice(0, 10)
        const normalizedDate = '2000' + isoDate.slice(4)

        byYear[record.year].source.push([
            isoDate,
            normalizedDate,
            record.month,
            record.distance !== null ? Math.round(record.distance * 100) / 100 : null,
            Math.round(record.cumsum_month * 100) / 100,
            Math.round(record.cumsum_year * 100) / 100,
            record.speed !== null ? record.speed : null,
            record.duration_seconds !== null ? record.duration_seconds : null,
            Math.round(record.cumsum_duration_month * 100) / 100
        ])
    })
    
    return Object.values(byYear)
} 